import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageLang } from '@/hooks/usePageLang';

export default function ArticlePage() {
  const { t } = useTranslation('article');
  const navigate = useNavigate();
  const params = useParams();
  const slug = params.slug || '';
  const { currentLang, handleLanguageChange } = usePageLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const sections = [
    { id: 'what', titleKey: 'article1_section1_title', contentKey: 'article1_section1_content' },
    { id: 'access', titleKey: 'article1_section2_title', contentKey: 'article1_section2_content' },
    { id: 'navigate', titleKey: 'article1_section3_title', contentKey: 'article1_section3_content' },
    { id: 'filter', titleKey: 'article1_section4_title', contentKey: 'article1_section4_content' },
    { id: 'seller', titleKey: 'article1_section5_title', contentKey: 'article1_section5_content' },
    { id: 'qc', titleKey: 'article1_section6_title', contentKey: 'article1_section6_content' },
    { id: 'compare', titleKey: 'article1_section7_title', contentKey: 'article1_section7_content' },
    { id: 'tips', titleKey: 'article1_section8_title', contentKey: 'article1_section8_content' },
  ];

  const faqs = [
    { q: 'article1_faq_1_q', a: 'article1_faq_1_a' },
    { q: 'article1_faq_2_q', a: 'article1_faq_2_a' },
    { q: 'article1_faq_3_q', a: 'article1_faq_3_a' },
    { q: 'article1_faq_4_q', a: 'article1_faq_4_a' },
    { q: 'article1_faq_5_q', a: 'article1_faq_5_a' },
  ];

  const related = [
    { title: 'article1_related_1', slug: 'allchinabuy-qc-guide' },
    { title: 'article1_related_2', slug: 'allchinabuy-shipping-comparison' },
    { title: 'article1_related_3', slug: 'finding-trusted-sellers-allchinabuy' },
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLang={currentLang} onLanguageChange={handleLanguageChange} solid />
      
      <article className="pt-20 md:pt-24">
        {/* Article Header */}
        <header className="bg-background-100 border-b border-background-200/40">
          <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-4xl mx-auto py-10 md:py-16">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
              <Link to={`/${currentLang}/`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                {t('article1_breadcrumb_home')}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>
              <Link to={`/${currentLang}/blog/`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                {t('article1_breadcrumb_blog')}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>
              <span className="text-foreground-700 font-medium">{t('article1_breadcrumb_current')}</span>
            </nav>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-md uppercase tracking-wider">
                Spreadsheet
              </span>
              <span className="text-foreground-400 text-sm">{t('article1_date')}</span>
              <span className="text-foreground-400 text-sm">•</span>
              <span className="text-foreground-400 text-sm">{t('article1_read_time')}</span>
            </div>
            
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 leading-snug mb-4">
              {t('article1_h1')}
            </h1>
            <p className="text-foreground-500 text-sm">{t('article1_author')}</p>
            <p className="text-foreground-400 text-xs mt-1">{t('article1_updated')}</p>
          </div>
        </header>

        {/* Article Hero Image */}
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-16">
          <div className="relative rounded-xl overflow-hidden aspect-[21/9] md:aspect-[3/1] mt-8 md:mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Open%20laptop%20showing%20a%20product%20spreadsheet%20with%20thumbnail%20photos%20and%20handwritten%20notes%20on%20a%20clean%20bright%20desk%20with%20coffee%20productive%20workspace%20photography%20high%20detail%20modern%20setup&width=1200&height=400&seq=article-hero-001&orientation=landscape&nocache=true"
              alt="AllChinaBuy Spreadsheet Guide"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Article Body */}
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-4xl mx-auto py-10 md:py-16">
          <div className="grid lg:grid-cols-[1fr_280px] gap-10 lg:gap-16">
            {/* Main Content */}
            <div className="order-2 lg:order-1">
              {/* Introduction */}
              <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-10 md:mb-12">
                {t('article1_intro')}
              </p>

              {/* Table of Contents */}
              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/40 mb-10 md:mb-12">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  {t('article1_toc')}
                </h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors text-left"
                      >
                        {t(section.titleKey)}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={() => scrollToSection('faq')}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors text-left"
                    >
                      {t('article1_toc_faq')}
                    </button>
                  </li>
                </ul>
              </div>

              {/* Sections */}
              {sections.map((section, index) => (
                <div key={section.id} id={section.id} className="mb-10 md:mb-12">
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                    {index + 1}. {t(section.titleKey)}
                  </h2>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    {t(section.contentKey)}
                  </p>
                </div>
              ))}

              {/* FAQ Section */}
              <div id="faq" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                  {t('article1_faq_title')}
                </h2>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/40">
                      <h3 className="font-semibold text-foreground-900 mb-2 text-sm md:text-base">
                        Q: {t(faq.q)}
                      </h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        A: {t(faq.a)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-primary-50 rounded-xl p-6 md:p-8 border border-primary-200/40 mb-10 md:mb-12">
                <p className="text-foreground-700 text-base leading-relaxed mb-5">
                  {t('article1_cta_text')}
                </p>
                <button
                  onClick={() => navigate(`/${currentLang}/`)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors"
                >
                  {t('article1_cta_button')}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              {/* Related Resources */}
              <div className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-5">
                  {t('article1_related_title')}
                </h2>
                <div className="space-y-3">
                  {related.map((item) => (
                    <button
                      key={item.slug}
                      onClick={() => navigate(`/${currentLang}/blog/${item.slug}/`)}
                      className="w-full text-left bg-background-100 rounded-lg p-4 border border-background-200/40 hover:border-primary-300/60 transition-colors group"
                    >
                      <span className="text-primary-600 text-sm font-medium group-hover:text-primary-700 transition-colors">
                        {t(item.title)}
                      </span>
                      <i className="ri-arrow-right-line ml-2 text-primary-400 group-hover:translate-x-1 transition-transform inline-block"></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* Internal Link to Home */}
              <div className="border-t border-background-200/40 pt-6">
                <p className="text-foreground-500 text-sm">
                  Back to{' '}
                  <Link to={`/${currentLang}/`} className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    AllChinaBuy Spreadsheet Home
                  </Link>
                  {' '}for more resources, guides, and product categories.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="order-1 lg:order-2">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Quick Links */}
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/40">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
                    Quick Links
                  </h3>
                  <ul className="space-y-2.5">
                    <li>
                      <Link to={`/${currentLang}/allchinabuy-spreadsheet/`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                        <i className="ri-table-line text-foreground-400"></i>
                        Spreadsheet Hub
                      </Link>
                    </li>
                    <li>
                      <Link to={`/${currentLang}/shipping-cost/`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                        <i className="ri-truck-line text-foreground-400"></i>
                        Shipping Guide
                      </Link>
                    </li>
                    <li>
                      <Link to={`/${currentLang}/safety/`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                        <i className="ri-shield-check-line text-foreground-400"></i>
                        Safety Guide
                      </Link>
                    </li>
                    <li>
                      <Link to={`/${currentLang}/review/`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                        <i className="ri-star-line text-foreground-400"></i>
                        AllChinaBuy Review
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* CTA to Main Site */}
                <div className="bg-accent-50 rounded-xl p-5 border border-accent-200/40">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                    AllChinaBuy Spreadsheet
                  </h3>
                  <p className="text-foreground-500 text-sm mb-4">
                    Visit our main site for the full product catalog and shopping experience.
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 font-label font-semibold text-xs rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap"
                  >
                    Visit Main Site
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer currentLang={currentLang} />
    </div>
  );
}